import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Download, Paperclip, Plus, Search } from 'lucide-react'
import { getExpenses } from '../../api/operatingCosts'
import Spinner from '../../components/Spinner'
import { FilterChips, StatusBadge } from '../../components/PageParts'
import { fmtDay, localToday } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { useSession } from '../../session'
import { money } from '../sales/salesShared'
import ExpenseTabs from './ExpenseTabs'
import { KIND_LABEL, PERIODS, periodRange, total } from './expensesShared'

const KINDS = [
  { id: 'all', label: 'All' },
  { id: 'entered', label: 'Recorded here' },
  { id: 'journal', label: 'Journal entries' },
  { id: 'auto', label: 'Automatic' },
]
const KIND_TONE = { entered: 'info', journal: 'muted', auto: 'muted' }

// Every indirect cost in a period, by expense account: what was recorded
// here, journal entries made in Accounting, and what the system records by
// itself.
export default function ExpenseList() {
  const { roles, has } = useSession()
  const books = has('accounting')  // without it, categories by name: no codes or journal entries
  const seesBooks = (roles.includes('admin') || roles.includes('accounting')) && books
  const payPage = (roles.includes('admin') || roles.includes('purchase')) && has('purchase') ? '/purchase/ap-ledger' : ((roles.includes('admin') || roles.includes('accounting')) && has('accounting') ? '/accounting/ap/payment' : '')
  const [params, setParams] = useSearchParams()
  const period = PERIODS.some((p) => p.id === params.get('period')) ? params.get('period') : 'month'
  const kind = KINDS.some((k) => k.id === params.get('show')) ? params.get('show') : 'all'
  const setParam = (key, value, fallback) => setParams((prev) => {
    const next = new URLSearchParams(prev)
    if (value === fallback) next.delete(key); else next.set(key, value)
    return next
  }, { replace: true })
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [account, setAccount] = useState('')
  const [search, setSearch] = useState('')

  useEffect(() => {
    setLoading(true)
    const [start, end] = periodRange(period)
    getExpenses({ start, end })
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [period])

  const counts = useMemo(() => Object.fromEntries(KINDS.map((k) => [k.id, rows.filter((r) => k.id === 'all' || r.kind === k.id).length])), [rows])
  const accounts = useMemo(() => [...new Map(rows.map((r) => [r.account_code, r.account])).entries()].sort(), [rows])
  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    return rows.filter((r) => (kind === 'all' || r.kind === kind)
      && (!account || r.account_code === account)
      && (!term || [r.what, r.category, r.account_code, r.vendor_name, r.paid_from, docNo('je', r.je_no)].join(' ').toLowerCase().includes(term)))
  }, [rows, kind, account, search])

  const exportCsv = () => {
    const head = books
      ? ['Date', 'What', 'Category', 'Account code', 'Account', 'From', 'Paid to', 'Paid from', 'Amount', 'Still owed', 'Journal entry']
      : ['Date', 'What', 'Paid to', 'Paid with', 'Amount', 'Still owed']
    const lines = shown.map((r) => (books
      ? [r.date, r.what, r.category, r.account_code, r.account, KIND_LABEL[r.kind], r.vendor_name, r.paid_from, Number(r.amount).toFixed(2), r.bill_id ? Number(r.owed).toFixed(2) : '', docNo('je', r.je_no)]
      : [r.date, r.what, r.vendor_name, r.paid_from, Number(r.amount).toFixed(2), r.bill_id ? Number(r.owed).toFixed(2) : '']))
    const csv = [head, ...lines].map((a) => a.map((x) => `"${String(x ?? '').replaceAll('"', '""')}"`).join(',')).join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `expenses_${localToday()}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="ci-page">
      <ExpenseTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Expenses</h1>
            <div className="ci-title-meta">The indirect costs of running the business. Costs of goods and services sold aren’t included.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <button type="button" className="ci-btn ci-btn-ghost" onClick={exportCsv} disabled={!shown.length}><Download size={15} />Export</button>
          <Link className="ci-btn ci-btn-primary" to="/operating/new"><Plus size={15} />Record an expense</Link>
        </div>
      </div>

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search expenses">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={books ? 'What, account or vendor…' : 'What or vendor…'} />
        </label>
        <select className="ci-select" value={period} onChange={(e) => setParam('period', e.target.value, 'month')} aria-label="Period">
          {PERIODS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        {books && (
          <select className="ci-select" value={account} onChange={(e) => setAccount(e.target.value)} aria-label="Account">
            <option value="">All accounts</option>
            {accounts.map(([code, name]) => <option key={code} value={code}>{code} · {name}</option>)}
          </select>
        )}
      </div>

      <FilterChips options={KINDS.filter((k) => books || k.id !== 'journal').map((k) => ({ ...k, count: counts[k.id] }))} value={kind} onChange={(id) => setParam('show', id, 'all')} />

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table fit-sm">
            <thead>
              <tr>
                <th>Date</th>
                <th>What</th>
                <th className="hide-sm">Paid to</th>
                <th className="hide-sm">Paid from</th>
                <th className="center hide-sm">From</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6"><Spinner label="Loading expenses…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="6"><div className="ci-empty">{rows.length ? 'No expenses match.' : 'No expenses in this period.'}</div></td></tr>
              ) : shown.map((r) => (
                <tr key={`${r.je_id}-${r.account_code}`}>
                  <td className="ci-muted-cell">{fmtDay(r.date)}</td>
                  <td>
                    <Link className="ci-strong exp-row-link" to={`/operating/expenses/${r.je_id}`}>{r.what || r.category}</Link>
                    {r.files > 0 && <span className="exp-clip" title={`${r.files} ${r.files === 1 ? 'file' : 'files'} attached`}><Paperclip size={12} aria-hidden="true" />{r.files}</span>}
                    <span className="cell-sub">
                      {r.kind === 'auto' ? r.category : books ? `${r.account_code} ${r.account}` : ''}
                      {seesBooks && <> · <Link to={`/accounting/journal/${r.je_id}`}>{docNo('je', r.je_no)}</Link></>}
                    </span>
                  </td>
                  <td className="hide-sm">{r.vendor_name || '–'}</td>
                  <td className="hide-sm ci-muted-cell">
                    {r.paid_from || '–'}
                    {r.bill_id && (
                      <span className="cell-sub">
                        {r.owed > 0 ? `${money(r.owed)} still owed` : 'Paid'}
                        {r.owed > 0 && payPage && <> · <Link to={`${payPage}?vendor_id=${r.vendor_id}`}>Pay</Link></>}
                      </span>
                    )}
                  </td>
                  <td className="center hide-sm"><StatusBadge label={KIND_LABEL[r.kind]} tone={KIND_TONE[r.kind]} /></td>
                  <td className="num ci-total">{money(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination">
          <span className="ci-page-info">{shown.length} {shown.length === 1 ? 'expense' : 'expenses'} · {money(total(shown))}</span>
        </div>
      </div>
    </div>
  )
}
